<template>
  <div class="home">
    <!--
      LESSON: Composing a page from section components
      In beauty-box, page_index.hbs included partials:
        {{> partials/page_index/section_hero}}
        {{> partials/page_index/section_about}}
        ...etc

      In Vue, the exact same composition looks like this — each section is a
      component receiving its data as props from the store via mapGetters.

      The mental model is identical: a page is just a list of sections. The
      difference is that Vue sections are reactive components, not static HTML.
    -->
    <!--
      LESSON: v-if guards on async data
      Each section only renders when its data is ready. During SSR this is
      instant (serverPrefetch runs first). On the client, the store is already
      hydrated from window.__INITIAL_STATE__, so the v-if is true on first paint.
      The only time a section is skipped is if the API call failed entirely.
    -->
    <template v-for="(offer, i) in offerData" :key="'offer-' + i">
      <SectionOffer v-if="isActive(offer)" :offer-data="offer" />
    </template>
    <template v-for="(hero, i) in heroData" :key="i">
      <SectionHero v-if="isActive(hero)" :hero-data="hero" />
    </template>
    <template v-for="(section, i) in customSectionsData" :key="'custom-' + i">
      <SectionCustom v-if="isActive(section)" :section-data="section" />
    </template>


  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import config from 'config';
import SectionHero from '../components/SectionHero';
import SectionOffer from '../components/SectionOffer';
import SectionCustom from '../components/SectionCustom';

export default {
  name: 'Home',

  components: {
    SectionHero,
    SectionOffer,
    SectionCustom,
  },

  computed: {
    ...mapGetters(['heroData', 'offerData', 'customSectionsData']),
    baseAssetsUrl: () => config.baseAssetsUrl,
    now() {
      return this.$route?.query?.nowDate ? new Date(this.$route.query.nowDate) : new Date();
    },
  },

  methods: {
    isActive(section) {
      const start = section.starttime ? new Date(section.starttime) : null;
      const end = section.endtime ? new Date(section.endtime) : null;
      const id = section.id || '(no id)';
      if (start && isNaN(start)) {
        console.warn(`[gift-card-rewards] Section "${id}" has invalid starttime — hidden.`);
        return false;
      }
      if (end && isNaN(end)) {
        console.warn(`[gift-card-rewards] Section "${id}" has invalid endtime — hidden.`);
        return false;
      }
      if (start && end && start >= end) {
        console.warn(`[gift-card-rewards] Section "${id}" has starttime >= endtime — hidden.`);
        return false;
      }
      if (start && this.now < start) return false;
      if (end && this.now >= end) return false;
      return true;
    },
  },

  /**
   * LESSON: serverPrefetch()
   *
   * This hook is called by @vue/server-renderer DURING renderToString, before
   * the component's markup is generated. The renderer awaits the returned Promise,
   * so by the time Home.vue's template is evaluated, the store already has data.
   *
   * Flow:
   *   Server: serverPrefetch → fetchApiData → SET_API_DATA → render template
   *           → framework serializes store.state → injects window.__INITIAL_STATE__
   *
   *   Client: store hydrated from __INITIAL_STATE__ → mounted() sees data exists → skip fetch
   *
   * The result: zero loading state visible to users, same as beauty-box's server-injected approach.
   */
  async serverPrefetch() {
    await this.$store.dispatch('fetchApiData');
  },

  mounted() {
    this.$store.dispatch('fetchApiData');
  },
};
</script>

<style lang="scss" inlineStyle>
/* beauty-box/dev/scss/common/typography.scss
   Font-face declarations for Macys Sans + Ramillas.
   The ~appSCSS Foundation import provides Foundation variables but not these
   campaign-specific fonts — we must declare them here. */
@font-face {
  font-family: 'Macys Sans Regular';
  font-display: swap;
  src:
    url('//storage.googleapis.com/digital-media-fonts/macys-sans/MacysSans.woff2') format('woff2'),
    url('//storage.googleapis.com/digital-media-fonts/macys-sans/MacysSans.woff') format('woff');
}
@font-face {
  font-family: 'Macys Sans Light';
  font-display: swap;
  src:
    url('//storage.googleapis.com/digital-media-fonts/macys-sans/MacysSansLt.woff2') format('woff2'),
    url('//storage.googleapis.com/digital-media-fonts/macys-sans/MacysSansLt.woff') format('woff');
}
@font-face {
  font-family: 'Macys Sans Medium';
  font-display: swap;
  src:
    url('//storage.googleapis.com/digital-media-fonts/macys-sans/MacysSansMd.woff2') format('woff2'),
    url('//storage.googleapis.com/digital-media-fonts/macys-sans/MacysSansMd.woff') format('woff');
}
@font-face {
  font-family: 'Macys Sans Bold';
  font-display: swap;
  src:
    url('//storage.googleapis.com/digital-media-fonts/macys-sans/MacysSansBd.woff2') format('woff2'),
    url('//storage.googleapis.com/digital-media-fonts/macys-sans/MacysSansBd.woff') format('woff');
}
@font-face {
  font-family: 'Macys Sans XBold';
  font-display: swap;
  src:
    url('//storage.googleapis.com/digital-media-fonts/macys-sans/MacysSansXBd.woff2') format('woff2'),
    url('//storage.googleapis.com/digital-media-fonts/macys-sans/MacysSansXBd.woff') format('woff');
}
@font-face {
  font-family: 'Ramillas Regular';
  font-display: swap;
  src:
    url('//storage.googleapis.com/digital-media-fonts/tt-ramillas/TTRamillasRegular.woff2') format('woff2'),
    url('//storage.googleapis.com/digital-media-fonts/tt-ramillas/TTRamillasRegular.woff') format('woff');
}

@font-face {
  font-family: 'Ramillas Medium';
  src: url('//storage.googleapis.com/digital-media-fonts/tt-ramillas/TTRamillasMedium500.eot?#iefix') format('eot'), 
        url('//storage.googleapis.com/digital-media-fonts/tt-ramillas/TTRamillasMedium500.woff2') format('woff2'), 
        url('//storage.googleapis.com/digital-media-fonts/tt-ramillas/TTRamillasMedium500.woff') format('woff');
}

@font-face {
  font-family: 'TT Ramillas Bold';
  font-display: swap;
  src:
    url('//storage.googleapis.com/digital-media-fonts/tt-ramillas/TTRamillasBold.woff2') format('woff2'),
    url('//storage.googleapis.com/digital-media-fonts/tt-ramillas/TTRamillasBold.woff') format('woff');
}

/* beauty-box/dev/scss/common/resets.scss
   Scoped to #campaign so Foundation's global resets don't bleed in or get
   overridden. All layout assumptions in beauty-box CSS depend on these. */
#campaign {
  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }

  body,
  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  button {
    margin: 0;
    padding: 0;
    letter-spacing: 0;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  a[href] {
    cursor: pointer;
  }
  a[href=''] {
    cursor: default !important;
  }
  a,
  a:focus,
  a:hover {
    text-decoration: none;
  }

  img {
    display: block;
    max-width: 100%;
  }

  svg {
    width: 100%;
    height: 100%;
    overflow: visible !important;
  }

  figure {
    margin: 0;
  }
  picture {
    display: block;
  }
  button {
    outline: none;
  }

  label {
    text-transform: none;
    padding: 0;
  }

  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  a {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-weight: normal;
  }
}

/* beauty-box/dev/scss/common/global_variables.scss */
:root {
  --loader-bg: #f7e9ce;
  --coupon-sticker-bg: #fffcf2;
  --coupon-sticker-font-color: #fff;
  --next-box-sticker-bg: #fc979b;
  --subscribe-cta-bg: #fff;
  --subscribe-cta-font-color: #000;
  --current-box-font-color: #000;
  --current-box-left-bg: transparent;
  --current-box-right-bg: transparent;
}

/* beauty-box/dev/scss/common/global_classes.scss */
html {
  text-size-adjust: 100%;
}

html body {
  font-family: 'Macys Sans Regular', sans-serif;
  line-height: 1;
}

body,
#campaign {
  overflow-x: clip;
}

#campaign {
  a {
    display: inline-block;
    text-transform: none;
  }

  a[href=''] {
    cursor: default !important;
  }

  .italic {
    font-style: italic;
  }

  .cqi {
    container-type: inline-size;
    width: 100%;
  }

  .link {
    text-decoration: underline !important;
  }

  section:not(:first-of-type) {
    margin-top: 80px;
  }

  .section-title {
    font-family: 'Macys Sans Bold', sans-serif;
    font-size: clamp(19px, 1.5cqi, 25px);
    line-height: 1.2;
    margin-bottom: clamp(20px, 2cqi, 30px);
  }

  .section-copy {
    font-family: 'Macys Sans Regular', sans-serif;
    font-size: max(14px, 3cqi);
    line-height: 1.3;
  }

  /* beauty-box/dev/scss/page_index/page.scss */
  [page='index'] {
    .inner {
      max-width: calc(1440px + 20px * 2);
      padding: 0 20px;
      margin: auto;
    }
  }
}

/* Fade-in animation classes — replaces GSAP/ScrollTrigger from FadeinOnScroll.js */
.anim-ready.anim--slideIn,
.anim-ready.anim--fadeIn {
  opacity: 0;
  transform: translateY(40px);
  transition:
    opacity 1s ease,
    transform 1s ease;
}

.anim-ready.anim--zoomIn figure img {
  transition: transform 1s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.anim-ready.anim--fromLeft {
  opacity: 0;
  transform: translate(-70px, 40px);
  transition:
    opacity 1s ease,
    transform 1s ease;
}

.anim-ready.anim--fromRight {
  opacity: 0;
  transform: translateX(70px);
  transition:
    opacity 1s ease,
    transform 1s ease;
}

.anim-ready.anim-visible {
  opacity: 1;
  transform: none;
}

/* Responsive */
@media (min-width: 376px) {
  .mobile-only {
    display: none !important;
  }
}

@media (max-width: 375px) {
  #campaign {
    .desktop-only {
      display: none !important;
    }

    section:not(:first-of-type) {
      margin-top: 64px !important;
    }

    .section-title {
      font-size: 24px;
    }
  }
}

.home {
  position: inherit;
}

/* Required by shop-the-look script: it sets body.disable-scroll + style="top: -Xpx"
   to lock scroll position while the overlay is open. Without position:fixed the
   page jumps to the top. */
body.disable-scroll {
  position: fixed !important;
}
</style>
